import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getToken, refreshMe, useAuth } from '../../lib/authStore';

/**
 * Wrap any route that requires authentication. While we bootstrap (i.e. confirm
 * the persisted JWT still works by hitting /me) we render nothing — that
 * prevents a flash of the protected UI before we know the session is valid.
 *
 * `adminOnly` redirects non-admin users to the home page.
 */
export default function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const loc = useLocation();
  const [checking, setChecking] = useState(() => !!getToken() && !isAuthenticated);

  // If we have a token but no user yet, fetch /me once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getToken() && !isAuthenticated) {
        await refreshMe();
        if (!cancelled) setChecking(false);
      } else {
        setChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (checking) {
    return (
      <div data-testid="auth-bootstrapping" className="min-h-screen flex items-center justify-center text-neutral-400 text-[12px] uppercase tracking-[0.22em] font-bold">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // First-login gate — the admin issued a temp password. Force the user
  // through /set-password before ANY protected route renders. They
  // still have a valid JWT so if they close the tab and return, they
  // land right back on this screen (option 3b from the rollout convo).
  if (user?.must_change_password && loc.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}
